import React, { useEffect, useRef, useState } from "react";
import "./styles/particleNetwork.scss";

/**
 * Global Site-Wide Particle Network
 * Ultra-fast 2D canvas with zero-allocation spatial grid (linked-list cell binning),
 * stroke batching (reduces draw calls from ~100 to 3), DPI awareness (capped at 2),
 * visibilitychange lifecycle pausing, hardware-adaptive frame rate, and gentle mouse interaction.
 */
const PARTICLE_CONFIG = {
  // Density: 1 particle per ~11,000 px^2 of viewport area
  densityAreaPx: 11000,
  maxParticlesDesktop: 130,
  maxParticlesMobile: 50,
  connectionDistance: 125, // px distance threshold for connecting lines
  baseSpeed: 0.40,
  particleRadiusMin: 0.9,
  particleRadiusMax: 2.0,
  mouseRadius: 130,
  mouseForce: 1.3,
  particleColor: "rgba(56, 189, 248, 0.45)", // Sky cyan dots
  lineBaseAlpha: 0.16, // Max line alpha at dist 0
  strokeColorLow: "rgba(56, 189, 248, 0.05)",
  strokeColorMid: "rgba(56, 189, 248, 0.10)",
  strokeColorHigh: "rgba(56, 189, 248, 0.16)",
  lineWidth: 0.75,
  targetFpsDesktop: 60,
  targetFpsMobile: 40,
  fadeInDurationMs: 1200,
};

function ParticleNetwork({ introFinished }) {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window !== "undefined") {
      if (
        sessionStorage.getItem("portfolio_intro_seen") === "true" ||
        introFinished === true
      ) {
        return true;
      }
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return true;
      }
    }
    return false;
  });

  // Soft fade in when intro finishes
  useEffect(() => {
    if (introFinished) {
      setIsVisible(true);
    }
  }, [introFinished]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const isTouchOnly =
      window.matchMedia &&
      (window.matchMedia("(pointer: coarse)").matches ||
        window.matchMedia("(hover: none)").matches);

    const isMobileDevice =
      window.innerWidth <= 768 ||
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);

    const targetFps = isMobileDevice
      ? PARTICLE_CONFIG.targetFpsMobile
      : PARTICLE_CONFIG.targetFpsDesktop;
    const frameIntervalMs = 1000 / targetFps;

    let width = 0;
    let height = 0;
    let dpr = 1;

    // Viewport mouse coordinates (-9999 when off-screen)
    const mouse = { x: -9999, y: -9999, radius: PARTICLE_CONFIG.mouseRadius };

    class Particle {
      constructor(w, h) {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        const angle = Math.random() * Math.PI * 2;
        const speed = (Math.random() * 0.5 + 0.5) * PARTICLE_CONFIG.baseSpeed;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.radius =
          Math.random() *
            (PARTICLE_CONFIG.particleRadiusMax - PARTICLE_CONFIG.particleRadiusMin) +
          PARTICLE_CONFIG.particleRadiusMin;
      }

      update(w, h) {
        this.x += this.vx;
        this.y += this.vy;

        // Bounce gently at screen edges
        if (this.x < 0) {
          this.x = 0;
          this.vx *= -1;
        } else if (this.x > w) {
          this.x = w;
          this.vx *= -1;
        }

        if (this.y < 0) {
          this.y = 0;
          this.vy *= -1;
        } else if (this.y > h) {
          this.y = h;
          this.vy *= -1;
        }

        // Mouse proximity reaction (desktop fine pointer only)
        if (!isTouchOnly && mouse.x > -1000) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distSq = dx * dx + dy * dy;
          const mouseRadiusSq = mouse.radius * mouse.radius;

          if (distSq < mouseRadiusSq && distSq > 0.001) {
            const dist = Math.sqrt(distSq);
            const force =
              ((mouse.radius - dist) / mouse.radius) * PARTICLE_CONFIG.mouseForce;
            this.x -= (dx / dist) * force;
            this.y -= (dy / dist) * force;
          }
        }
      }
    }

    let particles = [];
    const cellSize = PARTICLE_CONFIG.connectionDistance;
    const maxDist = PARTICLE_CONFIG.connectionDistance;
    const maxDistSq = maxDist * maxDist;

    // Spatial grid buffers (reused every frame to prevent GC pressure)
    let gridCols = 0;
    let gridRows = 0;
    let gridHead = new Int32Array(0);
    let particleNext = new Int32Array(0);

    const calculateParticleCount = (w, h) => {
      const area = w * h;
      const count = Math.floor(area / PARTICLE_CONFIG.densityAreaPx);
      const maxCap =
        w <= 768
          ? PARTICLE_CONFIG.maxParticlesMobile
          : PARTICLE_CONFIG.maxParticlesDesktop;
      return Math.max(25, Math.min(count, maxCap));
    };

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      const targetCount = calculateParticleCount(width, height);
      if (particles.length === 0) {
        for (let i = 0; i < targetCount; i++) {
          particles.push(new Particle(width, height));
        }
      } else if (particles.length < targetCount) {
        while (particles.length < targetCount) {
          particles.push(new Particle(width, height));
        }
      } else if (particles.length > targetCount) {
        particles.length = targetCount;
      }

      // Reposition any particles outside resized boundary
      particles.forEach((p) => {
        if (p.x > width) p.x = Math.random() * width;
        if (p.y > height) p.y = Math.random() * height;
      });

      // Allocate spatial grid arrays once per resize
      gridCols = Math.ceil(width / cellSize) + 1;
      gridRows = Math.ceil(height / cellSize) + 1;
      gridHead = new Int32Array(gridCols * gridRows);
      particleNext = new Int32Array(particles.length);
    };

    handleResize();

    // Mouse tracking for fine pointer devices
    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    if (!isTouchOnly) {
      window.addEventListener("mousemove", handleMouseMove, { passive: true });
      document.addEventListener("mouseleave", handleMouseLeave, { passive: true });
    }

    window.addEventListener("resize", handleResize, { passive: true });

    // If reduced motion is preferred, render one static frame and stop
    if (prefersReducedMotion) {
      ctx.clearRect(0, 0, width, height);
      ctx.beginPath();
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distSq = dx * dx + dy * dy;
          if (distSq < maxDistSq) {
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
          }
        }
      }
      ctx.strokeStyle = PARTICLE_CONFIG.strokeColorMid;
      ctx.lineWidth = PARTICLE_CONFIG.lineWidth;
      ctx.stroke();

      ctx.beginPath();
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        ctx.moveTo(p.x + p.radius, p.y);
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      }
      ctx.fillStyle = PARTICLE_CONFIG.particleColor;
      ctx.fill();

      return () => {
        window.removeEventListener("resize", handleResize);
        if (!isTouchOnly) {
          window.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseleave", handleMouseLeave);
        }
      };
    }

    let animationFrameId = null;
    let isTabActive = !document.hidden;
    let lastFrameTime = performance.now();

    // 4 Directional neighbor offsets: (col+1, row), (col-1, row+1), (col, row+1), (col+1, row+1)
    const neighborOffsets = [
      [1, 0],
      [-1, 1],
      [0, 1],
      [1, 1],
    ];

    const render = (now) => {
      if (!isTabActive) {
        animationFrameId = null;
        return;
      }

      animationFrameId = requestAnimationFrame(render);

      // Frame throttle for low-power and smooth frame pacing
      const elapsed = now - lastFrameTime;
      if (elapsed < frameIntervalMs) return;
      lastFrameTime = now - (elapsed % frameIntervalMs);

      ctx.clearRect(0, 0, width, height);

      // Reset spatial grid head
      gridHead.fill(-1);

      const pCount = particles.length;
      if (particleNext.length !== pCount) {
        particleNext = new Int32Array(pCount);
      }

      // Update particles and insert into zero-allocation linked list grid
      for (let i = 0; i < pCount; i++) {
        const p = particles[i];
        p.update(width, height);

        const col = Math.floor(p.x / cellSize);
        const row = Math.floor(p.y / cellSize);
        if (col >= 0 && col < gridCols && row >= 0 && row < gridRows) {
          const cellIndex = col + row * gridCols;
          particleNext[i] = gridHead[cellIndex];
          gridHead[cellIndex] = i;
        } else {
          particleNext[i] = -1;
        }
      }

      // Batch lines into 3 alpha buckets to reduce ctx.stroke() calls from ~100 to 3
      // Bucket 0: dist > 0.66 * maxDist (faint)
      // Bucket 1: 0.33 * maxDist < dist <= 0.66 * maxDist (mid)
      // Bucket 2: dist <= 0.33 * maxDist (bright)
      const pathLow = new Path2D();
      const pathMid = new Path2D();
      const pathHigh = new Path2D();

      const thirdDistSq = (maxDist * 0.33) * (maxDist * 0.33);
      const twoThirdsDistSq = (maxDist * 0.66) * (maxDist * 0.66);

      for (let r = 0; r < gridRows; r++) {
        for (let c = 0; c < gridCols; c++) {
          const cellIdx = c + r * gridCols;
          let i = gridHead[cellIdx];

          while (i !== -1) {
            const pA = particles[i];

            // 1. Check against other particles in the same cell
            let j = particleNext[i];
            while (j !== -1) {
              const pB = particles[j];
              const dx = pA.x - pB.x;
              const dy = pA.y - pB.y;
              const distSq = dx * dx + dy * dy;

              if (distSq < maxDistSq) {
                const targetPath =
                  distSq < thirdDistSq
                    ? pathHigh
                    : distSq < twoThirdsDistSq
                    ? pathMid
                    : pathLow;
                targetPath.moveTo(pA.x, pA.y);
                targetPath.lineTo(pB.x, pB.y);
              }
              j = particleNext[j];
            }

            // 2. Check against particles in the 4 forward neighbor cells
            for (let k = 0; k < 4; k++) {
              const nc = c + neighborOffsets[k][0];
              const nr = r + neighborOffsets[k][1];
              if (nc >= 0 && nc < gridCols && nr >= 0 && nr < gridRows) {
                let nj = gridHead[nc + nr * gridCols];
                while (nj !== -1) {
                  const pB = particles[nj];
                  const dx = pA.x - pB.x;
                  const dy = pA.y - pB.y;
                  const distSq = dx * dx + dy * dy;

                  if (distSq < maxDistSq) {
                    const targetPath =
                      distSq < thirdDistSq
                        ? pathHigh
                        : distSq < twoThirdsDistSq
                        ? pathMid
                        : pathLow;
                    targetPath.moveTo(pA.x, pA.y);
                    targetPath.lineTo(pB.x, pB.y);
                  }
                  nj = particleNext[nj];
                }
              }
            }

            i = particleNext[i];
          }
        }
      }

      // Draw batched lines in 3 passes
      ctx.lineWidth = PARTICLE_CONFIG.lineWidth;
      ctx.strokeStyle = PARTICLE_CONFIG.strokeColorLow;
      ctx.stroke(pathLow);

      ctx.strokeStyle = PARTICLE_CONFIG.strokeColorMid;
      ctx.stroke(pathMid);

      ctx.strokeStyle = PARTICLE_CONFIG.strokeColorHigh;
      ctx.stroke(pathHigh);

      // Batch all particle dots into a single fill() call
      ctx.beginPath();
      for (let i = 0; i < pCount; i++) {
        const p = particles[i];
        ctx.moveTo(p.x + p.radius, p.y);
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      }
      ctx.fillStyle = PARTICLE_CONFIG.particleColor;
      ctx.fill();
    };

    animationFrameId = requestAnimationFrame(render);

    const handleVisibilityChange = () => {
      isTabActive = !document.hidden;
      if (isTabActive && !animationFrameId) {
        lastFrameTime = performance.now();
        animationFrameId = requestAnimationFrame(render);
      } else if (!isTabActive && animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (!isTouchOnly) {
        window.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`site-particle-network ${isVisible ? "is-visible" : "is-hidden"}`}
      aria-hidden="true"
    />
  );
}

export default React.memo(ParticleNetwork);
